import { useRef, useCallback, useEffect, forwardRef, useImperativeHandle } from "react";
import Editor, { OnMount } from "@monaco-editor/react";
import { MonacoBinding } from "y-monaco";
import { yText, ydoc, getAwareness } from "../collab/yjsDoc";
import { CADPLM_THEME, THEME_NAME } from "./monacoTheme";
import type * as Monaco from "monaco-editor";

const DEFAULT_SCRIPT = `const { makeBaseBox, makeSphere } = replicad;

function main() {
  const size = param("size", 20, { min: 5, max: 50 });
  const radius = param("radius", 13, { min: 1, max: 30 });
  const box = makeBaseBox(size, size, size);
  const sphere = makeSphere(radius);
  return box.cut(sphere);
}
`;

export interface ScriptEditorRef {
  jumpToLine: (line: number, col?: number) => void;
}

interface ScriptEditorProps {
  onExecute: (script: string) => void;
  debounceMs?: number;
  savedScript?: string;
  readOnly?: boolean;
  banner?: string;
  errorMsg?: string;
}

const ScriptEditor = forwardRef<ScriptEditorRef, ScriptEditorProps>(({
  onExecute,
  debounceMs = 400,
  savedScript,
  readOnly = false,
  banner,
  errorMsg,
}, ref) => {
  const bindingRef = useRef<MonacoBinding | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const monacoRef = useRef<typeof Monaco | null>(null);
  const editorRef = useRef<Monaco.editor.IStandaloneCodeEditor | null>(null);
  const modelRef = useRef<Monaco.editor.ITextModel | null>(null);

  useImperativeHandle(ref, () => ({
    jumpToLine: (line: number, col: number = 1) => {
      const editor = editorRef.current;
      if (editor) {
        editor.revealLineInCenter(line);
        editor.setPosition({ lineNumber: line, column: col });
        editor.focus();
      }
    }
  }), []);

  const handleChange = useCallback(
    (value: string | undefined) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        if (value) onExecute(value);
      }, debounceMs);
    },
    [onExecute, debounceMs]
  );

  const handleMount: OnMount = useCallback(
    (editor, monaco) => {
      monacoRef.current = monaco;
      editorRef.current = editor;
      if (yText.length === 0) {
        const initial = savedScript || DEFAULT_SCRIPT;
        ydoc.transact(() => {
          yText.insert(0, initial);
        });
      }

      const model = editor.getModel();
      if (model) {
        modelRef.current = model;
        bindingRef.current = new MonacoBinding(yText, model, new Set([editor]), getAwareness() ?? undefined);
      }

      monaco.editor.defineTheme(THEME_NAME, CADPLM_THEME);
      monaco.editor.setTheme(THEME_NAME);

      const initialValue = editor.getValue();
      if (initialValue) onExecute(initialValue);
    },
    [onExecute, savedScript]
  );

  useEffect(() => {
    const monaco = monacoRef.current;
    const model = modelRef.current;
    if (!monaco || !model) return;

    if (!errorMsg) {
      monaco.editor.setModelMarkers(model, "cadplm", []);
      return;
    }

    // Try to extract line:col from V8 error stacks: "<anonymous>:N:M"
    const match = errorMsg.match(/<anonymous>:(\d+):(\d+)/);
    if (match) {
      const line = Math.max(1, parseInt(match[1], 10));
      const col = Math.max(1, parseInt(match[2], 10));
      monaco.editor.setModelMarkers(model, "cadplm", [{
        startLineNumber: line,
        startColumn: col,
        endLineNumber: line,
        endColumn: model.getLineMaxColumn(line),
        message: errorMsg,
        severity: monaco.MarkerSeverity.Error,
      }]);
    } else {
      monaco.editor.setModelMarkers(model, "cadplm", []);
    }
  }, [errorMsg]);

  useEffect(() => {
    return () => {
      bindingRef.current?.destroy();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <>
      {banner && (
        <div className="script-editor-banner">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="12" height="12">
            <circle cx="8" cy="8" r="6" />
            <path d="M8 5v3M8 10v1" />
          </svg>
          {banner}
        </div>
      )}
    <Editor
      height={banner ? "calc(100% - 28px)" : "100%"}
      defaultLanguage="javascript"
      theme="vs-dark"
      onMount={handleMount}
      onChange={handleChange}
      options={{
        minimap: { enabled: false },
        fontSize: 13,
        fontFamily: "'DM Mono', 'SF Mono', 'Fira Code', monospace",
        fontLigatures: true,
        lineNumbers: "on",
        scrollBeyondLastLine: false,
        automaticLayout: true,
        tabSize: 2,
        wordWrap: "on",
        renderLineHighlight: "line",
        cursorBlinking: "smooth",
        cursorSmoothCaretAnimation: "on",
        smoothScrolling: true,
        padding: { top: 12, bottom: 12 },
        lineDecorationsWidth: 12,
        overviewRulerBorder: false,
        hideCursorInOverviewRuler: true,
        scrollbar: {
          verticalScrollbarSize: 6,
          horizontalScrollbarSize: 6,
          useShadows: false,
        },
        bracketPairColorization: { enabled: true },
        readOnly,
      }}
    />
    </>
  );
});

export default ScriptEditor;
